/**
 * Alternating Category Row - Renders category entries with different visual styles
 * Created by neo on 21.01.26
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { ExploreEntry } from '../../Model/Explore/ExploreEntry';
import { Recipe } from '../../Model/Diet/Recipe/Recipe';
import { CinematicCard } from './CinematicCard';
import { TileCard } from './TileCard';
import { MagazineCard } from './MagazineCard';
import { PortraitCard } from './PortraitCard';

export type CategoryStyle = 'cinematic' | 'tiles' | 'magazine' | 'portrait';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const RowContainer = styled.div`
  width: 100%;
  animation: ${fadeInUp} 0.5s ease-out forwards;
`;

// Cinematic: Full-width slider with large cards
const CinematicGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 24px;
  padding: 8px;
  margin: -8px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }

  > * {
    will-change: transform;
  }
`;

// Tiles: 3-4 column grid of square cards
const TilesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  padding: 8px;
  margin: -8px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 500px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  > * {
    will-change: transform;
  }
`;

// Magazine: Stacked horizontal cards
const MagazineGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  padding: 8px;
  margin: -8px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }

  > * {
    will-change: transform;
  }
`;

// Portrait: Horizontal scroll of tall cards
const PortraitScrollContainer = styled.div`
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: -16px;
    bottom: 0;
    width: 30px;
    background: linear-gradient(to left, rgba(250, 250, 250, 1) 0%, transparent 50%);
    pointer-events: none;
    z-index: 1;
  }
`;

const PortraitScroll = styled.div`
  display: flex;
  gap: 20px;
  overflow-x: auto;
  overflow-y: visible;
  padding: 16px;
  padding-bottom: 32px;
  margin: -16px;
  margin-bottom: -32px;
  scroll-snap-type: x mandatory;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  > div {
    scroll-snap-align: start;
    flex: 0 0 200px;

    @media (max-width: 600px) {
      flex: 0 0 160px;
    }
  }
`;

const CardWrapper = styled.div<{ delay: number }>`
  animation: ${fadeInUp} 0.5s ease-out forwards;
  animation-delay: ${({ delay }) => delay}ms;
  opacity: 0;
`;

export type AlternatingCategoryRowProps = {
  entries: (ExploreEntry | Recipe)[];
  cardStyle: CategoryStyle;
  linkPrefix?: string;
};

export const AlternatingCategoryRow: React.FC<AlternatingCategoryRowProps> = observer(
  ({ entries, cardStyle, linkPrefix = '/academy/' }) => {
    const renderCards = () => {
      switch (cardStyle) {
        case 'cinematic':
          return (
            <CinematicGrid>
              {entries.slice(0, 4).map((entry, index) => (
                <CardWrapper key={entry.id} delay={index * 100}>
                  <CinematicCard entry={entry} linkPrefix={linkPrefix} />
                </CardWrapper>
              ))}
            </CinematicGrid>
          );

        case 'tiles':
          return (
            <TilesGrid>
              {entries.slice(0, 8).map((entry, index) => (
                <CardWrapper key={entry.id} delay={index * 50}>
                  <TileCard entry={entry} linkPrefix={linkPrefix} />
                </CardWrapper>
              ))}
            </TilesGrid>
          );

        case 'magazine':
          return (
            <MagazineGrid>
              {entries.slice(0, 6).map((entry, index) => (
                <CardWrapper key={entry.id} delay={index * 80}>
                  <MagazineCard entry={entry} linkPrefix={linkPrefix} />
                </CardWrapper>
              ))}
            </MagazineGrid>
          );

        case 'portrait':
          return (
            <PortraitScrollContainer>
              <PortraitScroll>
                {entries.slice(0, 10).map((entry, index) => (
                  <CardWrapper key={entry.id} delay={index * 60}>
                    <PortraitCard entry={entry} linkPrefix={linkPrefix} />
                  </CardWrapper>
                ))}
              </PortraitScroll>
            </PortraitScrollContainer>
          );

        default:
          return null;
      }
    };

    return <RowContainer>{renderCards()}</RowContainer>;
  },
);

export const getStyleForIndex = (index: number): CategoryStyle => {
  const styles: CategoryStyle[] = ['cinematic', 'tiles', 'magazine', 'portrait'];
  return styles[index % styles.length];
};
